import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {
  StyledVendorDetails,
  StyledVendorDetailsContentSpace,
  StyledVendorDetailsCollapse
} from './StyledVendorDetails';
import VendorBankDetails from '../VendorBankDetails';
import VendorContactDetails from '../VendorContactDetails';
import VendorDetailsPanel from '../VendorDetailsPanel';
import VendorInvoices from '../VendorInvoices';
import VendorSubscriptions from '../VendorSubscriptions';
import EditButton from '../../../EditButton';
import ExportVendorTransactionsModal from '../ExportVendorTransactionsModal';
import Spin from '../../../Spin';
import AddPaymentModal from '../../TransactionsPage/AddPaymentModal';
import {invoicesHelpers} from '../../../../utils/helpers';
import {DEFAULT_MODAL_PROPS} from '../../../../constants';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {subscriptionActions} from '../../../../state/actions/subscription.actions';

const collapseProps = {
  collapsible: 'header',
  defaultActiveKey: ['1']
};

const collapseItemProps = {key: '1'};

const VendorDetails = ({
  onBack,
  onDelete,
  onEdit,
  onStatusChange,
  vendor,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [invoices, setInvoices] = useState({loading: true, outstanding: [], paid: []});
  const [subscriptions, setSubscriptions] = useState({data: [], loading: true});
  const [addPaymentModalProps, setAddPaymentModalProps] = useState(DEFAULT_MODAL_PROPS);
  const dispatch = useDispatch();

  const hasBeneficiary = useMemo(() => Boolean(vendor?.beneficiary_id), [vendor?.beneficiary_id]);

  const editButton = (
    <EditButton
      onClick={(e) => onEdit && onEdit(e)}
    />
  );

  const enabledDelete = useMemo(() => {
    return !invoices?.loading && invoices?.outstanding?.length === 0 && invoices?.paid?.length === 0 &&
      !subscriptions?.loading && subscriptions?.data?.length === 0;
  }, [invoices, subscriptions]);

  useEffect(() => {
    loadVendorInformation(vendor);
  }, [vendor?.vendor_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVendorInformation = (vendor) => {
    const id = vendor?.vendor_id;
    if (!id) return;

    loadInvoices(id);
    loadSubscriptions(id);
  }

  const loadInvoices = (id) => {
    dispatch(invoicesActions.getInvoices({
      query: {vendor_local_id: id},
      successCallback: ({invoices: data}) => {
        let [outstanding, paid] = data.reduce(([out, paid], invoice) => {
          invoicesHelpers.isInvoiceSettled(invoice.payment_status) ? paid.push(invoice) : out.push(invoice);
          return [out, paid];
        }, [[], []]);

        setInvoices({loading: false, outstanding, paid});
      },
      errorCallback: () => setInvoices({loading: false, outstanding: [], paid: []})
    }));
  }

  const loadSubscriptions = (id) => {
    dispatch(subscriptionActions.getVendorSubscriptions({
      id,
      successCallback: ({subscriptions: data}) => setSubscriptions({data, loading: false}),
      errorCallback: () => setSubscriptions({data: [], loading: false})
    }));
  }

  const handleOpenAddPaymentModal = () => setAddPaymentModalProps({...addPaymentModalProps, open: true});

  const handleCancelAddPaymentModal = () => setAddPaymentModalProps(DEFAULT_MODAL_PROPS);

  const handleOkAddPaymentModal = () => loadVendorInformation(vendor);

  return (
    <StyledVendorDetails {...rest}>
      <VendorDetailsPanel
        enableAddPayment={hasBeneficiary}
        enabledDelete={enabledDelete}
        onAddPayment={handleOpenAddPaymentModal}
        onBack={onBack}
        onDelete={onDelete}
      >
        <StyledVendorDetailsContentSpace size='middle' direction='vertical'>
          <StyledVendorDetailsCollapse
            {...collapseProps}
            items={[
              {
                ...collapseItemProps,
                children: <VendorContactDetails vendor={vendor} onStatusChange={onStatusChange} />,
                label: t('contactDetails'),
                extra: editButton
              }
            ]}
          />
          <StyledVendorDetailsCollapse
            {...collapseProps}
            items={[
              {
                ...collapseItemProps,
                children: <VendorBankDetails vendor={vendor} />,
                label: t('bankDetails'),
                extra: editButton
              }
            ]}
          />

          {subscriptions.data.length > 0 && (
            <Spin spinning={subscriptions.loading} size='xsmall'>
              <StyledVendorDetailsCollapse
                {...collapseProps}
                items={[
                  {
                    ...collapseItemProps,
                    children: <VendorSubscriptions data={subscriptions.data} />,
                    label: t('subscriptions'),
                  }
                ]}
              />
            </Spin>
          )}

          {invoices.outstanding.length > 0 && (
            <Spin spinning={invoices.loading} size='xsmall'>
              <StyledVendorDetailsCollapse
                {...collapseProps}
                items={[
                  {
                    ...collapseItemProps,
                    children: <VendorInvoices data={invoices.outstanding} />,
                    label: t('outstanding'),
                  }
                ]}
              />
            </Spin>
          )}

          {invoices.paid.length > 0 && (
            <Spin spinning={invoices.loading} size='xsmall'>
              <StyledVendorDetailsCollapse
                {...collapseProps}
                items={[
                  {
                    ...collapseItemProps,
                    children: <VendorInvoices data={invoices.paid} />,
                    label: t('paid'),
                    extra: <ExportVendorTransactionsModal vendor={vendor} />
                  }
                ]}
              />
            </Spin>
          )}

        </StyledVendorDetailsContentSpace>
      </VendorDetailsPanel>

      <AddPaymentModal
        {...addPaymentModalProps}
        initialFormData={{vendor_local_id: vendor?.vendor_id}}
        onCancel={handleCancelAddPaymentModal}
        onSuccess={handleOkAddPaymentModal}
      />

    </StyledVendorDetails>
  );
}

VendorDetails.propTypes = {
  onBack: PropTypes.func,
  onEdit: PropTypes.func,
  onStatusChange: PropTypes.func,
  vendor: PropTypes.shape({
    beneficiary_id: PropTypes.string,
    vendor_id: PropTypes.string
  }),
}

export default VendorDetails;
